import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { FaArrowDown, FaArrowUp, FaHome, FaClipboardCheck, FaPlaneArrival, FaPlaneDeparture, FaHotel, FaBook, FaTimesCircle } from "react-icons/fa";
import { Tabs, Tab, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useGlobalData } from "../../../context/GlobalDataContext";
import { useTranslation } from "react-i18next";
import moment from "moment";
import DeparturesTab from "../components/DeparturesTab";
import InHouseTab from "../components/InHouseTab";
import Swal from "sweetalert2";
import axios from "axios";
import CryptoJS from "crypto-js"; // Import CryptoJS for encryption

import { renderDate } from "../utils/dateFormatter";



const FrontDeskDashboard = () => {
  const maximizeBtnRef = useRef(null);
  const statsRef = useRef(null);
  const activityStatsRef = useRef(null);
  const guestRef = useRef(null);
  const { dateformat, company } = useGlobalData();
  const navigate = useNavigate();
  const business_date = localStorage.getItem("business_date") || moment().format("YYYY-MM-DD");
  const secretKey = process.env.REACT_APP_ENCRYPTION_SECRET_KEY || "abde100199199111";

  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingActivity, setIsLoadingActivity] = useState(true);
  const [isLoadingGuests, setIsLoadingGuests] = useState(true);
  const [isFullScreen, setFullScreen] = useState(false);
  const [leftStats, setLeftStats] = useState([]);
  const [activityStats, setActivityStats] = useState([]);
  const [guestData, setGuestData] = useState([]);
  const [occupancyStats, setOccupancyStats] = useState({ booked: 0, available: 0, blocked: 0, occupancy: 0 });
  const [selectedDate, setSelectedDate] = useState(moment(business_date));

  const { t, i18n } = useTranslation();
  const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
  const dayName = moment(selectedDate).format("dddd");

  const formattedBusinessDate = moment(selectedDate).format("MMMM D, YYYY");

  // Debounce utility
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };


  const handleCheckIn = async (guest, index) => {
    const token = localStorage.getItem("token");

    Swal.fire({
      title: `Proceed with Check-in?`,
      text: `Do you want to check-in ${guest.name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Check-in!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Show loading while processing
          Swal.fire({
            title: "Processing Check-in...",
            text: "Please wait...",
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          // Prepare request data for check-in
          const requestData = {
            bookingId: guest.booking_id,
            guestName: guest.name,
            phone: guest.phone,
            email: guest.email,
            nationality: guest.nationality,
            checkInDate: guest.checkIn,
            roomNumber: guest.roomNumber,
            roomType: guest.roomType,
            nights: guest.nights,
            reservationType: guest.reservationType,
            bookingSource: guest.bookingSource,
          };

          // Call Check-in API
          const response = await axios.post(
            `${apiURL}/booking/guest-checkin`,
            requestData,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.status === 200) {
            Swal.fire({
              title: "Check-in Successful!",
              text: `${guest.name} is now checked in.`,
              icon: "success",
            }).then(() => {
              // Remove the guest from the table after successful check-in
              setGuestData((prevData) => prevData.filter((_, i) => i !== index));
            });
          } else {
            throw new Error("Unexpected response status");
          }
        } catch (error) {
          console.error("Check-in Error:", error);
          Swal.fire({
            title: "Check-in Failed!",
            text: error.response?.data?.message || "An error occurred during check-in.",
            icon: "error",
          });
        }
      }
    });
  };

  // Memoized API fetch functions
  const loadOccupancyStats = useCallback(
    debounce(async () => {
      setIsLoadingStats(true);
      const token = localStorage.getItem("token");

      try {
        const response = await fetch(`${apiURL}/booking/occupancy-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();

        setLeftStats([
          { label: t("Occupied"), value: `${data.occupiedRooms}/${data.totalRooms}`, color: "#ffc107" },
          { label: t("Available"), value: data.availableRooms.toString(), color: "#007bff" },
          { label: t("Unavailable"), value: data.unavailableRooms.toString(), color: "#dc3545" },
        ]);

        setOccupancyStats({  
          booked: data.occupiedRooms,
          available: data.availableRooms,
          blocked: data.unavailableRooms,
          occupancy: data.totalRooms > 0 ? Math.round((data.occupiedRooms / data.totalRooms) * 100) : 0,
        });
      } catch (error) {
        console.error("Error fetching occupancy stats:", error);
        setLeftStats([
          { label: t("Occupied"), value: "0/0", color: "#ffc107" },
          { label: t("Available"), value: "0", color: "#007bff" },
          { label: t("Unavailable"), value: "0", color: "#dc3545" },
        ]);
        setOccupancyStats({ booked: 0, available: 0, blocked: 0, occupancy: 0 });
      } finally {
        setIsLoadingStats(false);
      }
    }, 300),
    [apiURL, t]
  );

  const loadActivityStats = useCallback(
    debounce(async () => {
      setIsLoadingActivity(true);
      const token = localStorage.getItem("token");

      try {
        const response = await fetch(`${apiURL}/booking/activity-stats`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();

        // Map API labels to translated labels
        const labelMap = {
          "Arrivals": "Arrivals",
          "Departures": "Departures",
          "New Bookings": "New Bookings",
          "Stay Overs": "Stay Overs",
          "Revenue Today": "Revenue Today",
          "Cancelled Bookings": "Cancelled Bookings",
          "Guests (Adults/Children)": "Guests (Adults/Children)"
        };

        const translatedStats = (data.activityStats || []).map(stat => ({
          ...stat,
          label: t(labelMap[stat.label] || stat.label) // Translate known labels, fallback to original
        }));

        setActivityStats(translatedStats.length > 0 ? translatedStats : [
          { label: t("Arrivals"), value: "0/0", color: "#007bff" },
          { label: t("Departures"), value: "0/0", color: "#17a2b8" },
          { label: t("New Bookings"), value: "0", color: "#28a745" },
          { label: t("Stay Overs"), value: "0", color: "#ffc107" },
          { label: t("Revenue Today"), value: "0", color: "#6f42c1" },
          { label: t("Cancelled Bookings"), value: "0", color: "#dc3545" },
          { label: t("Guests (Adults/Children)"), value: "0/0", color: "#fd7e14" },
        ]);
      } catch (error) {
        console.error("Error fetching activity stats:", error);
        setActivityStats([
          { label: t("Arrivals"), value: "0/0", color: "#007bff" },
          { label: t("Departures"), value: "0/0", color: "#17a2b8" },
          { label: t("New Bookings"), value: "0", color: "#28a745" },
          { label: t("Stay Overs"), value: "0", color: "#ffc107" },
          { label: t("Revenue Today"), value: "0", color: "#6f42c1" },
          { label: t("Cancelled Bookings"), value: "0", color: "#dc3545" },
          { label: t("Guests (Adults/Children)"), value: "0/0", color: "#fd7e14" },
        ]);
      } finally {
        setIsLoadingActivity(false);
      }
    }, 300),
    [apiURL, t]
  );

  const loadGuestData = useCallback(
    debounce(async () => {
      setIsLoadingGuests(true);
      const token = localStorage.getItem("token");

      try {
        const response = await fetch(`${apiURL}/booking/reservations?arrddate=${selectedDate.format("YYYY-MM-DD")}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();

        const transformedData = data.reservations.map((reservation) => ({
          name: reservation.guest_name,
          phone: reservation.guest_phone,
          accommodation: `${reservation.room_types} - ${reservation.room_numbers}`,
          stay: {
            nights: Math.ceil((new Date(reservation.check_out_date) - new Date(reservation.check_in_date)) / (1000 * 60 * 60 * 24)),
            adults: 1,
            children: 0,
          },
          checkIn: reservation.check_in_date,
          checkOut: reservation.check_out_date,
          booking_status : reservation.booking_status,	
          booking_id	 : reservation.booking_id,	
          status: reservation.booking_status === "reserved" ? t("Due") : reservation.booking_status === "cancelled" ? t("Cancelled") : t("Check-Out"),
        }));

        setGuestData(transformedData);
      } catch (error) {
        console.error("Error fetching guest reservations:", error);
        setGuestData([]);
      } finally {
        setIsLoadingGuests(false);
      }
    }, 300),
    [apiURL, selectedDate, t]
  );

  // Re-fetch activity stats when language changes
  useEffect(() => {
    loadActivityStats();
  }, [i18n.language, loadActivityStats]);

  // Re-translate activityStats labels when language changes
  useEffect(() => {
    setActivityStats(prevStats => prevStats.map(stat => {
      const labelMap = {
        "Arrivals": "Arrivals",
        "Departures": "Departures",
        "New Bookings": "New Bookings",
        "Stay Overs": "Stay Overs",
        "Revenue Today": "Revenue Today",
        "Cancelled Bookings": "Cancelled Bookings",
        "Guests (Adults/Children)": "Guests (Adults/Children)"
      };
      return {
        ...stat,
        label: t(labelMap[stat.label] || stat.label) // Update label to translated version
      };
    }));
  }, [i18n.language, t]);

  // Fullscreen toggle effect
  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    if (maximizeBtn) maximizeBtn.addEventListener("click", handleClick);

    return () => {
      if (maximizeBtn) maximizeBtn.removeEventListener("click", handleClick);
      if (isFullScreen && document.exitFullscreen) document.exitFullscreen();
    };
  }, [isFullScreen]);

  // Consolidated data loading effect
  useEffect(() => {
    const observerOptions = { root: null, rootMargin: "0px", threshold: 0.1 };

    const statsObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        loadOccupancyStats();
        statsObserver.unobserve(statsRef.current);
      }
    }, observerOptions);

    const activityObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        loadActivityStats();
        activityObserver.unobserve(activityStatsRef.current);
      }
    }, observerOptions);

    const guestObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        loadGuestData();
        guestObserver.unobserve(guestRef.current);
      }
    }, observerOptions);

    if (statsRef.current) statsObserver.observe(statsRef.current);
    if (activityStatsRef.current) activityObserver.observe(activityStatsRef.current);
    if (guestRef.current) guestObserver.observe(guestRef.current);

    const fallbackTimer = setTimeout(() => {
      if (isLoadingStats) loadOccupancyStats();
      if (isLoadingActivity) loadActivityStats();
      if (isLoadingGuests) loadGuestData();
    }, 2000);

    return () => {
      if (statsRef.current) statsObserver.unobserve(statsRef.current);
      if (activityStatsRef.current) activityObserver.unobserve(activityStatsRef.current);
      if (guestRef.current) guestObserver.unobserve(guestRef.current);
      clearTimeout(fallbackTimer);
    };
  }, [loadOccupancyStats, loadActivityStats, loadGuestData]);

  // Navigation handler for View Details
  const handleViewDetails = (label) => {
    switch (label.toLowerCase()) {
      case t("arrivals").toLowerCase():
        navigate("/hospitality/guest-list-card");
        break;
      case t("departures").toLowerCase():
        navigate("/hospitality/list-depature-guest-card");
        break;
      case t("new bookings").toLowerCase():
        navigate("/hospitality/reservation-list");
        break;
      case t("cancelled bookings").toLowerCase():
        navigate("/hospitality/reservation-list");
        break;
      case t("stay overs").toLowerCase():
        navigate("/hospitality/inhouse-guest");
        break;
      default:
        navigate("/hospitality/daily-room-sold");
    }
  };

  // Memoized stats to prevent unnecessary recalculations
  const memoizedActivityStats = useMemo(() => activityStats, [activityStats]);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Page Header */}
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-4">
              <h3 className="page-title">{company}</h3>
            </div>
            <div className="col-md-8 float-end ms-auto">
              <div className="d-flex title-head">
                <div className="view-icons">
                  <button className="grid-view btn btn-link" title={t("Refresh")}>
                    <i className="las la-redo-alt" />
                  </button>
                  <button className="list-view btn btn-link" ref={maximizeBtnRef} title={t("Full Screen")}>
                    <i className="las la-expand-arrows-alt" />
                  </button>
                </div>
                <Link to="/hospitality/guest-reservation" className="btn add-btn custom-btn">
                  <i className="la la-plus-circle custom-icon" /> {t("Add New Reservations")}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Full-Row Stats */}
        <div className="stats-row" ref={activityStatsRef} style={{ minHeight: "120px" }}>
          {isLoadingActivity ? (
            <div className="col-12 text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">{t("Loading activity stats")}</p>
            </div>
          ) : (
            memoizedActivityStats.map((stat, index) => (
              <div key={index} className="stats-box" style={{ minWidth: "150px" }}>
                <div className="stats-border" style={{ backgroundColor: stat.color }}></div>
                <div className="stats-content">
                  <h5>{stat.value}</h5>
                  <p>{stat.label}</p>
                  <span
                    className="view-details"
                    style={{ cursor: "pointer", color: "#007bff" }}
                    onClick={() => handleViewDetails(stat.label)}
                  >
                    {t("View Details")}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Main Content */}
        <div className="row">
          {/* Left Panel */}
          <div className="col-md-4" style={{ height: "70vh", display: "flex", flexDirection: "column" }}>
            <div className="card" style={{ height: "100%" }}>
              <div className="card-header text-center" style={{ flex: "0 0 auto" }}>
                <h2 style={{ fontSize: "36px", fontWeight: "bold", marginBottom: "10px" }}>{dayName}</h2>
                <p style={{ fontSize: "18px", color: "#555" }}>{formattedBusinessDate}</p>
              </div>
              <div className="card-body" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                <div ref={statsRef} className="row mb-3" style={{ flex: "0 0 auto", minHeight: "150px" }}>
                  {isLoadingStats ? (
                    <div className="col-12 text-center py-4">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2 text-muted">{t("Loading occupancy stats")}</p>
                    </div>
                  ) : (
                    leftStats.map((stat, index) => (
                      <div key={index} className="col-4">
                        <div
                          className="left-stat-box text-center"
                          style={{
                            borderLeft: `5px solid ${stat.color}`,
                            backgroundColor: "#fff",
                            color: "#333",
                            borderRadius: "8px",
                            padding: "10px",
                            fontWeight: "bold",
                            marginBottom: "15px",
                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <h3 style={{ margin: 0, fontSize: "20px" }}>{stat.value}</h3>
                          <p style={{ margin: 0, fontSize: "14px", fontWeight: "normal" }}>{stat.label}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="occupancy-stats text-center" style={{ flex: "0 0 auto", marginTop: "auto" }}>
                  <div className="progress-ring">
                    <svg width="150" height="150">
                      <circle cx="75" cy="75" r="67.5" stroke="#e5e5e5" strokeWidth="15" fill="none" />
                      <circle
                        cx="75"
                        cy="75"
                        r="67.5"
                        stroke="#4caf50"
                        strokeWidth="15"
                        fill="none"
                        strokeDasharray={`${(occupancyStats.occupancy / 100) * (2 * Math.PI * 67.5)} ${2 * Math.PI * 67.5}`}
                        strokeDashoffset="0"
                        style={{ transition: "stroke-dasharray 0.5s ease" }}
                      />
                    </svg>
                    <span
                      style={{
                        position: "absolute",
                        fontSize: "42px",
                        fontWeight: "bold",
                        top: "60px",
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                    >
                      {occupancyStats.occupancy}%
                    </span>
                  </div>
                  <p style={{ marginTop: "15px", fontSize: "16px", fontWeight: "500" }}>{t("Occupancy")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel (Tabs with Table) */}
          <div className="col-md-8" style={{ height: "70vh", display: "flex", flexDirection: "column" }}>
            <div className="card" style={{ height: "100%" }}>
              <Tabs defaultActiveKey="arrivals" className="mb-3">
                <Tab
                  eventKey="arrivals"
                  title={
                    <span>
                      <FaPlaneArrival className="me-2" /> {t("Arrivals")}
                    </span>
                  }
                >
                  <div ref={guestRef} style={{ minHeight: "300px" }}>
                    {isLoadingGuests ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted">{t("Loading arrivals data")}</p>
                      </div>
                    ) : (
<table className="table">
        <thead>
          <tr>
            <th>{t("Guest")}</th>
            <th>{t("Accommodation")}</th>
            <th>{t("Stay")}</th>
            <th>{t("Status")}</th>
          </tr>
        </thead>
        <tbody>
          {guestData.map((guest, index) => {
            // Encrypt the booking_id
            const encryptedId = CryptoJS.AES.encrypt(
              guest.booking_id.toString(),
              secretKey
            ).toString();

            return (
              <tr key={index}>
                <td>
                  <Link
                    to={`/hospitality/reservation-details/${encodeURIComponent(encryptedId)}`}
                    className="colored-box reservation-no-box"
                    style={{ minWidth: "0px" }}
                  >
                    {guest.name}
                  </Link>
                </td>
                <td>{guest.accommodation}</td>
                <td>
                  {renderDate(guest.checkIn)} → {renderDate(guest.checkOut)}
                  <p>
                    {guest.stay.nights} {t("Night(s)")}, {guest.stay.adults} {t("Adult(s)")}, {guest.stay.children} {t("Child(ren)")}
                  </p>
                </td>
                <td>
                  {guest.booking_status === "reserved" ? (
                    <button
                    style={{
                      display: "inline-block",
                      padding: "4px 12px",
                      borderRadius: "4px",
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: "500",
                      marginTop: "8px",
                      textTransform: "capitalize",
                    }}
                      className="btn bg-primary" 
                      onClick={() => handleCheckIn(guest, index)}
                      
                    >
                      {t("Check-in")}
                    </button>
                  ) : (
                    <span>{guest.bookingStatus}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
                    )}
                  </div>
                </Tab>
                <Tab
                  eventKey="departures"
                  title={
                    <span>
                      <FaPlaneDeparture className="me-2" /> {t("Departures")}
                    </span>
                  }
                >
                  <DeparturesTab businessDate={moment(business_date).format("YYYY-MM-DD")} />
                </Tab>
                <Tab
                  eventKey="in-house"
                  title={
                    <span>
                      <FaHotel className="me-2" /> {t("In-House")}
                    </span>
                  }
                >
                  <InHouseTab businessDate={moment(business_date).format("YYYY-MM-DD")} />
                </Tab>
                <Tab
                  eventKey="new-bookings"
                  title={
                    <span>
                      <FaBook className="me-2" /> {t("New Bookings")}
                    </span>
                  }
                >
                  <div className="text-center py-4 text-muted">

        

                      <div className="no-data-container text-center mt-4">
                          <img
                            src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                            alt="No data available"
                            style={{ width: "150px", marginBottom: "20px" }}
                          />
                          <h3>{t("No New Bookings")}</h3>
                          <p>{t("There are no new bookings in the selected date range")}</p>

                      </div>
                 </div>

        
                </Tab>
                <Tab
                  eventKey="cancellations"
                  title={
                    <span>
                      <FaTimesCircle className="me-2" /> {t("Cancellations")}
                    </span>
                  }
                >
                  <div className="text-center py-4 text-muted">
        

                    <div className="no-data-container text-center mt-4">
                <img
                  src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                  alt="No data available"
                  style={{ width: "150px", marginBottom: "20px" }}
                />
                <h3>{t("No Cancellations")}</h3>
                <p>{t("There are no cancelled bookings in the selected date range")}</p>
    
              </div>
                  </div>
                </Tab>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrontDeskDashboard;